// Shared between AutomationRuleDialog.tsx (flat form) and the flow-graph
// editor (AutomationFlowNodes.tsx) — kept in one place so the two views of
// the same rule never drift out of sync on what fields/operators/actions
// are offered.

export const TICKET_EVENTS = new Set([
  'ticket_created', 'ticket_status_changed', 'ticket_assigned',
  'ticket_priority_changed', 'sla_at_risk', 'sla_breached',
]);

// 2026-09-01: moved here from AutomationRuleEditor.tsx so the Trigger node
// (now hosting the full rule-settings form, not just a read-only label --
// see TriggerFlowNode) can render the same event list without importing a
// constant out of a sibling component file.
export const EVENT_TYPES = [
  { value: 'conversation_created', label: 'Conversation Created' },
  { value: 'conversation_updated', label: 'Conversation Updated' },
  { value: 'message_created', label: 'Message Created' },
  { value: 'conversation_opened', label: 'Conversation Opened' },
  { value: 'ticket_created', label: 'Ticket Created' },
  { value: 'ticket_status_changed', label: 'Ticket Status Changed' },
  { value: 'ticket_assigned', label: 'Ticket Assigned' },
  { value: 'ticket_priority_changed', label: 'Ticket Priority Changed' },
  { value: 'sla_at_risk', label: 'Ticket SLA At Risk' },
  { value: 'sla_breached', label: 'Ticket SLA Breached' },
  // 2026-09-04: fired once a request_profile_field nudge is answered (or
  // skipped) -- see Docs/RELEASE_NOTES_2026-09-04_PROFILE_NUDGE.md. Not a
  // ticket-domain event (conversation_id/contact_id/field_key context only).
  { value: 'profile_field_captured', label: 'Profile Field Captured' },
  // 2026-09-10 (plan 08 Phase 1c): published from Digital Companion's
  // existing per-turn classifier call right after it returns -- zero new
  // LLM calls. Only fires when at least one configured Semantic Signal
  // (Automation -> Signals & Image Routing) actually matched this
  // message; context carries `signals` (flat string array, use with the
  // Signals field + a "contains" operator below) and `category_id`.
  { value: 'message_classified', label: 'Message Classified (Signals)' },
];

export const OPERATORS = [
  { value: 'equal_to', label: 'Equal to' },
  { value: 'not_equal_to', label: 'Not equal to' },
  { value: 'is_present', label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  // 2026-09-08: the backend condition engine (_apply_operator,
  // automation_engine.py) already supported these -- just never exposed
  // here. Useful for a numeric field like Incoming Message Count where
  // "at least N" is often what's actually wanted, not "exactly N".
  { value: 'greater_than_or_equal', label: 'Greater than or equal to' },
  { value: 'less_than_or_equal', label: 'Less than or equal to' },
  // 2026-09-10: the backend condition engine (_apply_operator,
  // automation_engine.py:114-124) already supported these too -- for a
  // list-shaped context field (today, only `signals`), not a plain
  // string. `expected_value` for these three is a JSON array string
  // (e.g. '["skin_image_needed"]'), which the Signals field's own
  // multiselect editor below writes for you -- never hand-typed JSON.
  { value: 'contains_any', label: 'Contains any of' },
  { value: 'contains_all', label: 'Contains all of' },
  { value: 'does_not_contain_any', label: 'Does not contain any of' },
];

export const CONDITION_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'assignee_id', label: 'Assignee' },
  { value: 'team_id', label: 'Team' },
  { value: 'inbox_id', label: 'Inbox' },
  { value: 'message_content', label: 'Message Content' },
  { value: 'contact_email', label: 'Contact Email' },
  { value: 'contact_phone', label: 'Contact Phone' },
  // 2026-09-04: matches profile_field_captured's context (the field_key that
  // was just answered/skipped, e.g. "id_card_photo") -- lets a rule branch
  // per field without a separate condition-field list for this one event.
  { value: 'field_key', label: 'Profile Field Key' },
  // 2026-09-08: matches message_created's context -- how many messages
  // the CUSTOMER (not the agent/bot) has sent in this conversation so
  // far, including the one that just triggered this rule. Use with
  // "Equal to" for "exactly the Nth message" (fires once, since the
  // count only ever increases) or "Greater than or equal to" for
  // "the Nth message or later" (keeps firing after, unless combined with
  // Execute Once Per Conversation).
  { value: 'incoming_message_count', label: 'Incoming Message Count' },
  // 2026-09-09 (user request: "count >= 5 and inactivity time = 1 minute
  // then it gets fired"): combine with incoming_message_count in the same
  // AND group for "send this once the customer has sent N messages AND
  // gone quiet for M minutes." Unlike every other condition, this one
  // ISN'T evaluated the instant the triggering message arrives (inactivity
  // is always 0 right then) -- its presence tells the backend to wait M
  // minutes and recheck (cancelling itself if a newer message shows up in
  // the meantime), then fire. The chosen operator doesn't matter for this
  // field, only the value does -- set it to the number of minutes to wait,
  // e.g. operator "Greater than or equal to", value "1".
  { value: 'inactivity_minutes', label: 'Inactivity (minutes)' },
  // 2026-09-10 (plan 08 Phase 1d): matches message_classified's `signals`
  // context field -- a flat array of this account's own configured
  // Semantic Signal keys that matched. Use with "Contains any of" /
  // "Contains all of" / "Does not contain any of" above; the value editor
  // for this field (see AutomationFlowNodes.tsx) is a multiselect sourced
  // from the account's real signals, never a raw JSON text box.
  { value: 'signals', label: 'Signals' },
];

// 2026-08-08: fields available in a ticket-domain event's context (see
// ticketing_service.py._publish_ticket_event's payload) — "status" and
// "priority" are shared with the conversation field list above (same key
// names, different domain), category_id/ticket_id/assigned_agent_id are
// ticket-specific.
export const TICKET_CONDITION_FIELDS = [
  { value: 'status', label: 'Ticket Status' },
  { value: 'priority', label: 'Ticket Priority' },
  { value: 'category_id', label: 'Category' },
  { value: 'assigned_agent_id', label: 'Assigned Agent' },
  { value: 'conversation_id', label: 'Conversation ID' },
  // 2026-08-09: set by Digital Companion's crisis-keyword detector during
  // counselling — "true"/"false" as a string (not a real bool), since a
  // condition's expected_value is always a string; compare with
  // operator "equal_to" and value "true".
  { value: 'is_safety_escalation', label: 'Safety Escalation' },
];

export const ACTION_TYPES = [
  { value: 'assign_agent', label: 'Assign to Agent' },
  { value: 'assign_team', label: 'Assign to Team' },
  { value: 'remove_assignee', label: 'Remove Assignee' },
  { value: 'add_label', label: 'Add Label' },
  { value: 'remove_label', label: 'Remove Label' },
  { value: 'change_status', label: 'Change Status' },
  { value: 'change_priority', label: 'Change Priority' },
  { value: 'resolve_conversation', label: 'Resolve Conversation' },
  { value: 'send_message', label: 'Send Message' },
  { value: 'notify_agent', label: 'Notify Assigned Agent' },
  { value: 'notify_manager', label: 'Notify Manager' },
  { value: 'escalate_ticket', label: 'Escalate Ticket' },
  { value: 'create_follow_up_ticket', label: 'Flag Follow-up Ticket' },
  // 2026-08-10: turns AI on/off for the conversation behind a ticket/event —
  // see Docs/DIGITAL_COMPANION_AI_HANDOFF_PLAN.md §2.7.
  { value: 'set_ai_enabled', label: 'Turn AI On/Off' },
  // 2026-08-26: pauses the rest of the chain, then resumes on its own —
  // e.g. "notify Team A, wait 2 hours, notify Team B." Top-level only (a
  // rule's own action list), not inside a branch case — see
  // automation_engine.py's _run_actions_from / schemas/automation.py's
  // rejection of a nested `wait`.
  { value: 'wait', label: 'Wait' },
  // 2026-08-31: urgently rings the target agent (in-app + WhatsApp, same
  // pipeline as a normal assignment notification) and surfaces the
  // customer's phone number in that notification so the agent can call
  // right back — e.g. "ticket created in category X -> assign agent A,
  // call agent A." See automation_actions.py's call_agent handler; auto-
  // dialing both legs together is a separate, not-yet-built follow-up.
  { value: 'call_agent', label: 'Call Agent (Urgent Notify)' },
  // 2026-09-04: sends a configurable nudge message and captures whatever
  // the customer replies with next (text or photo) against a free-text
  // field_key -- no hardcoded fields. See
  // Docs/RELEASE_NOTES_2026-09-04_PROFILE_NUDGE.md and
  // automation_actions.py's request_profile_field handler.
  { value: 'request_profile_field', label: 'Request Profile Field' },
  // 2026-09-21 (Docs/TOPIC_GUARDRAIL_PLAN.md): blocks the AI from
  // generating a real answer for this turn -- reply_text is sent instead.
  // Only takes effect when this is the first, single-action rule matching
  // the triggering signal (AutomationEngine.find_immediate_single_action_
  // match) -- a rule combining this with other actions never gets the
  // synchronous "block before the AI runs" treatment, same constraint
  // request_profile_field's own inline-clubbing already has. See
  // automation_actions.py's ignore_ai_message handler.
  { value: 'ignore_ai_message', label: 'Block AI Response (Topic Guardrail)' },
];

// 2026-09-07: suggestions only, never a restriction -- field_key stays
// free text everywhere it's used (the request_profile_field action's
// params, and a profile_field_captured condition's Value box), matching
// this feature's "no hardcoded fields" design. This is just a starting
// list of commonly-used keys so an admin doesn't have to remember/retype
// one exactly; not fetched from the backend (there's no fixed catalogue to
// fetch -- any string typed here becomes valid the moment a rule uses it).
export const COMMON_PROFILE_FIELD_KEYS = [
  'name', 'age', 'gender', 'id_card_photo',
  // 2026-09-08: added for the extract_fields suggestions (see
  // AutomationActionParamsEditor.tsx's "Fields to extract from the
  // photo" input) -- also valid as a plain field_key like the rest of
  // this list, same "suggestion only" posture.
  'address', 'id_number',
];

// 2026-09-09 (Document Vault -- generalizes photo capture beyond ID cards,
// see Docs/mockups/document-vault-lab-report.html): suggestions for the
// new document_type input, same "suggestion only, free text" posture as
// COMMON_PROFILE_FIELD_KEYS above -- any string typed here is valid.
export const COMMON_DOCUMENT_TYPES = [
  'id_card', 'lab_report', 'prescription', 'vaccination_card',
  // 2026-09-10: skin_condition/chest_xray are exact Image Type Registry
  // category keys (app/services/image_type_registry.py's
  // CATEGORY_DEFINITIONS) -- typing one of these here (not just
  // suggesting it) is what triggers automatic vision-model description on
  // upload, in addition to/instead of named-field extraction. See
  // AutomationActionParamsEditor.tsx's VisionRoutingHint for the inline
  // explanation shown when a typed document_type matches one of these.
  'skin_condition', 'chest_xray',
];

// 2026-09-10 (user: "for xray or skin there are no fields to be
// extracted, how does it supposed to reflect it there?"). These
// categories are photos with no named textual fields to extract at all --
// OCR/LLM field extraction (the "Fields to extract" panel) is meaningless
// for them, unlike lab_report where it's still genuinely useful alongside
// the automatic vision description. Manually kept in sync with
// image_type_registry.py's CATEGORY_DEFINITIONS -- a judgment call
// (which categories are "no structured fields") the backend doesn't
// expose as a flag.
export const IMAGE_TYPES_WITH_NO_EXTRACTABLE_FIELDS = ['skin_condition', 'chest_xray'];

export const EVENT_LABELS: Record<string, string> = {
  conversation_created: 'Conversation Created',
  conversation_updated: 'Conversation Updated',
  message_created: 'Message Created',
  conversation_opened: 'Conversation Opened',
  ticket_created: 'Ticket Created',
  ticket_status_changed: 'Ticket Status Changed',
  ticket_assigned: 'Ticket Assigned',
  ticket_priority_changed: 'Ticket Priority Changed',
  sla_at_risk: 'Ticket SLA At Risk',
  sla_breached: 'Ticket SLA Breached',
  profile_field_captured: 'Profile Field Captured',
  message_classified: 'Message Classified (Signals)',
};
